import { ESourceTransactionType } from 'enums';
import { IExpense } from 'interfaces';
import { csvEscape, excelSafeExport } from './csv';

// The expense list export. The import page reads this same file back, so both use this header.
export const EXPENSE_CSV_HEADER = [
  'Expense ID',
  'Expense Date',
  'Expense Category',
  'Warehouse',
  'Client/Supplier',
  'Transaction Type',
  'Payment Type',
  'Payment Method',
  'Amount',
  'Note',
];

export interface IExpenseRecord extends IExpense {
  expenseWarehouse: string;
  userName: string;
  typeOfExpense: string;
}

export const getExpenseRecord = (expense: IExpense): IExpenseRecord => {
  let expenseWarehouse = '';
  let userName = '';
  let typeOfExpense = ESourceTransactionType.Expense;

  if (expense.salesExpense && expense.salesExpense.length) {
    expenseWarehouse = expense.salesExpense[0].sales?.warehouse?.name ?? '';
    userName = expense.salesExpense[0].sales?.client?.name ?? '';
    typeOfExpense = ESourceTransactionType.Sales;
  }
  if (expense.purchaseExpense && expense.purchaseExpense.length) {
    expenseWarehouse = expense.purchaseExpense[0].purchase?.warehouse?.name ?? '';
    userName = expense.purchaseExpense[0].purchase?.supplier?.name ?? '';
    typeOfExpense = ESourceTransactionType.Purchases;
  }
  if (expense.returnExpense && expense.returnExpense.length) {
    expenseWarehouse = expense.returnExpense[0].return?.warehouse?.name ?? '';
    userName = expense.returnExpense[0].return?.user?.name ?? '';
    typeOfExpense = ESourceTransactionType.Return;
  }
  if (expense.damageExpense && expense.damageExpense.length) {
    expenseWarehouse = expense.damageExpense[0].damage?.warehouse?.name ?? '';
    userName = expense.damageExpense[0].damage?.user?.name ?? '';
    typeOfExpense = ESourceTransactionType.Damage;
  }
  if (expense.transferStockExpense && expense.transferStockExpense.length) {
    expenseWarehouse = expense.transferStockExpense[0].transferStock?.fromWarehouse?.name ?? '';
    userName = expense.transferStockExpense[0].transferStock?.createdBy?.name ?? '';
    typeOfExpense = ESourceTransactionType.StockTransfer;
  }

  return { ...expense, expenseWarehouse, userName, typeOfExpense };
};

export const getExpenseExportRow = (expense: IExpenseRecord) => [
  excelSafeExport(expense.id),
  new Date(expense.expenseDate).toLocaleDateString('en-GB'),
  csvEscape(expense.category?.name ?? ''),
  csvEscape(expense.expenseWarehouse ?? ''),
  csvEscape(expense.userName ?? ''),
  csvEscape(expense.typeOfExpense ?? ''),
  csvEscape(expense.paymentType ?? ''),
  csvEscape(expense.paymentMethod?.bankName ?? ''),
  csvEscape(String(expense.amount ?? '')),
  csvEscape(expense.note ?? ''),
];

export interface IExpenseImportRow {
  expenseDate: string;
  category: string;
  amount: string;
  paymentType: string;
  paymentMethod: string;
  note: string;
}

const requiredImportColumns = ['Expense Date', 'Expense Category', 'Payment Type', 'Amount'];

// One parsed CSV row, keyed by header. Expense ID, Warehouse, Client/Supplier and Transaction
// Type are ignored: an imported expense is a standalone expense. The API checks the values.
export const expenseCsvRecordToImportRow = (
  csvRecord: Record<string, string | undefined>,
): IExpenseImportRow => {
  const missingColumn = requiredImportColumns.find((column) => !(column in csvRecord));
  if (missingColumn) {
    throw new Error(`The file has no '${missingColumn}' column.`);
  }
  const read = (column: string) => (csvRecord[column] ?? '').trim();
  return {
    expenseDate: read('Expense Date'),
    category: read('Expense Category'),
    amount: read('Amount'),
    paymentType: read('Payment Type'),
    paymentMethod: read('Payment Method'),
    note: read('Note'),
  };
};
